import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Copy, Clock, Shield, BarChart3, Search } from "lucide-react";
import { useAuthCode } from "@/hooks/useAuthCode";

const AuthCodePanel = () => {
  const [selectedAccount, setSelectedAccount] = useState<'analytics' | 'audytor'>('analytics');
  const [generatedCode, setGeneratedCode] = useState<any>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const { 
    generateAuthCode, 
    isGenerating
  } = useAuthCode();

  // Odlicznik czasu
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (generatedCode && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setGeneratedCode(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [generatedCode, timeRemaining]);

  const handleGenerateCode = async () => {
    try {
      const result = await generateAuthCode({
        accountType: selectedAccount
      });
      
      // Ustaw wygenerowany kod
      if (result) {
        setGeneratedCode({
          token: result.token,
          expiresIn: result.expiresIn,
          accountType: selectedAccount
        });
        setTimeRemaining(result.expiresIn || 300);
      }
    } catch (error) {
      // Error handling is done in the hook
      console.error('Error generating auth code:', error);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Toast notification will be handled by the hook
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };


  const getAccountIcon = (type: 'analytics' | 'audytor') => {
    return type === 'analytics' ? BarChart3 : Search;
  };

  const getAccountColor = (type: 'analytics' | 'audytor') => {
    return type === 'analytics' ? 'bg-blue-500' : 'bg-emerald-500';
  };

  const getAccountLabel = (type: 'analytics' | 'audytor') => {
    return type === 'analytics' ? 'Analytics' : 'Audytor';
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="space-y-4">
        {/* Informacja na początku */}
        <div className="text-center bg-muted/50 rounded-lg p-6 mb-6 min-h-[120px] flex items-center justify-center">
          <p className="text-sm text-foreground font-medium">
            Podczas logowania do konta Gmail wybierz sposób weryfikacji za pomocą Google Authenticator
          </p>
        </div>

        {/* Wybór konta */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-muted-foreground">
            Wybierz konto:
          </Label>
          
          <div className="flex gap-2">
            {/* Analytics */}
            <button
              onClick={() => setSelectedAccount('analytics')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md border transition-colors ${
                selectedAccount === 'analytics' 
                  ? 'border-blue-500 bg-blue-500 text-white' 
                  : 'border-border hover:bg-muted'
              }`}
            >
              <BarChart3 className="h-4 w-4" />
              <span className="text-sm font-medium">Analytics</span>
            </button>

            {/* Audytor */}
            <button
              onClick={() => setSelectedAccount('audytor')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md border transition-colors ${
                selectedAccount === 'audytor' 
                  ? 'border-emerald-500 bg-emerald-500 text-white' 
                  : 'border-border hover:bg-muted'
              }`}
            >
              <Search className="h-4 w-4" />
              <span className="text-sm font-medium">Audytor</span>
            </button>
          </div>
        </div>

        {/* Przycisk generuj */}
        <Button 
          onClick={handleGenerateCode}
          disabled={isGenerating}
          className={`w-full py-3 ${
            selectedAccount === 'analytics' 
              ? 'bg-blue-600 hover:bg-blue-700' 
              : 'bg-emerald-600 hover:bg-emerald-700'
          }`}
        >
          {isGenerating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Generowanie...
            </>
          ) : (
            <>
              <Shield className="h-4 w-4 mr-2" />
              Generuj Kod
            </>
          )}
        </Button>

        {/* Wygenerowany kod */}
        {generatedCode && (
          <div className="bg-card border rounded-lg p-6 w-full min-h-[200px]">
            <div className="text-center mb-5">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className={`p-2 rounded ${getAccountColor(generatedCode.accountType)} text-white`}>
                  {React.createElement(getAccountIcon(generatedCode.accountType), { className: "h-4 w-4" })}
                </div>
                <span className="text-sm font-medium">
                  Kod dla {getAccountLabel(generatedCode.accountType)}
                </span>
              </div>
              <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span className={timeRemaining <= 10 ? 'text-red-500 font-semibold' : ''}>
                  Wygaśnie za {timeRemaining} sekund
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 p-3 bg-muted border rounded font-mono text-sm text-center">
                    {generatedCode.token}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(generatedCode.token)}
                    className="px-3 py-3"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthCodePanel;
